import type { JSX } from 'react';
import { useMemo, useState } from 'react';

import { Input } from './Input.js';

/**
 * Phone entry with a country dial-code picker (flag + code), Iraq-first.
 *
 * Lives in components/ui because every surface that takes a phone number now
 * uses it — the customer/supplier form (which the till's New-customer dialog
 * also renders), the field-rep workspace, the portal login, CRM contacts and
 * accounts, and the storefront. That matters beyond tidiness: the backend
 * resolves a customer by the LAST 9 DIGITS of their number, so two screens that
 * store the same person differently quietly become two customers.
 *
 * The picker owns the prefix and the box holds only the local part, which is
 * what makes a leading zero impossible rather than merely discouraged: staff
 * type 0750… exactly as they always have, the 0 is dropped as they type, and the
 * value leaves as +964750…. Numbers stay LTR even in the Arabic UI.
 *
 * Mirrored, deliberately, by apps/customer-mobile, apps/mandob-mobile and
 * apps/pos-desktop (`src/lib/countries.ts` in each) — those three are outside
 * the pnpm workspace and cannot import this. Change all four together.
 */
export interface Country {
  iso: string;
  flag: string;
  dial: string;
  name: string;
}

export const COUNTRIES: Country[] = [
  { iso: 'IQ', flag: '🇮🇶', dial: '964', name: 'Iraq' },
  { iso: 'TR', flag: '🇹🇷', dial: '90', name: 'Türkiye' },
  { iso: 'SY', flag: '🇸🇾', dial: '963', name: 'Syria' },
  { iso: 'JO', flag: '🇯🇴', dial: '962', name: 'Jordan' },
  { iso: 'LB', flag: '🇱🇧', dial: '961', name: 'Lebanon' },
  { iso: 'SA', flag: '🇸🇦', dial: '966', name: 'Saudi Arabia' },
  { iso: 'AE', flag: '🇦🇪', dial: '971', name: 'UAE' },
  { iso: 'KW', flag: '🇰🇼', dial: '965', name: 'Kuwait' },
  { iso: 'QA', flag: '🇶🇦', dial: '974', name: 'Qatar' },
  { iso: 'BH', flag: '🇧🇭', dial: '973', name: 'Bahrain' },
  { iso: 'OM', flag: '🇴🇲', dial: '968', name: 'Oman' },
  { iso: 'EG', flag: '🇪🇬', dial: '20', name: 'Egypt' },
  { iso: 'IR', flag: '🇮🇷', dial: '98', name: 'Iran' },
  { iso: 'US', flag: '🇺🇸', dial: '1', name: 'United States' },
  { iso: 'GB', flag: '🇬🇧', dial: '44', name: 'United Kingdom' },
  { iso: 'DE', flag: '🇩🇪', dial: '49', name: 'Germany' },
  { iso: 'SE', flag: '🇸🇪', dial: '46', name: 'Sweden' },
  { iso: 'NL', flag: '🇳🇱', dial: '31', name: 'Netherlands' },
];

/** Iraq — these shops are Iraqi, so it leads the list and is the default. */
export const DEFAULT_COUNTRY: Country = COUNTRIES[0]!;

/** Combine a dial code + a locally-typed number into +<dial><digits> (drops a leading 0). */
export function combinePhone(dial: string, local: string): string {
  const digits = local.replace(/\D/g, '').replace(/^0+/, '');
  return digits ? `+${dial}${digits}` : '';
}

/**
 * Which country an already-stored number belongs to, so EDITING one shows its
 * own flag instead of defaulting to Iraq and mis-reading the dial code as part
 * of the local number. Longest dial code wins: +964 must beat +96, and +1 must
 * not swallow +44.
 */
export function countryFromPhone(value: string | null | undefined): Country {
  const v = (value ?? '').replace(/[^\d+]/g, '');
  if (!v.startsWith('+')) return DEFAULT_COUNTRY;
  const digits = v.slice(1);
  let best: Country | null = null;
  for (const c of COUNTRIES) {
    if (digits.startsWith(c.dial) && (!best || c.dial.length > best.dial.length)) best = c;
  }
  return best ?? DEFAULT_COUNTRY;
}

interface Props {
  id: string;
  /** Full combined value (+9647…), kept in the parent like a plain input. */
  value: string;
  onChange: (full: string) => void;
  autoFocus?: boolean;
  autoComplete?: string;
  disabled?: boolean;
}

export function PhoneInput({ id, value, onChange, autoFocus, autoComplete = 'tel', disabled }: Props): JSX.Element {
  // Seeded from the value so an existing +90… number opens on Türkiye rather
  // than on Iraq with "90…" read as the start of the local number.
  const [iso, setIso] = useState(() => countryFromPhone(value).iso);
  const country = useMemo(() => COUNTRIES.find((c) => c.iso === iso) ?? DEFAULT_COUNTRY, [iso]);
  // Show only the local part in the field (the picker owns the prefix).
  const local = value.startsWith(`+${country.dial}`) ? value.slice(country.dial.length + 1) : value.replace(/^\+/, '');

  return (
    <div className="flex gap-2" dir="ltr">
      <select
        aria-label="Country"
        value={iso}
        disabled={disabled}
        onChange={(e) => {
          const next = COUNTRIES.find((c) => c.iso === e.target.value) ?? DEFAULT_COUNTRY;
          setIso(next.iso);
          onChange(combinePhone(next.dial, local));
        }}
        className="h-10 shrink-0 rounded-md border border-border bg-bg-subtle ps-2 pe-1 text-sm outline-none transition focus:border-brand disabled:cursor-not-allowed disabled:opacity-70"
      >
        {COUNTRIES.map((c) => (
          <option key={c.iso} value={c.iso}>
            {c.flag} +{c.dial}
          </option>
        ))}
      </select>
      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        value={local}
        onChange={(e) => onChange(combinePhone(country.dial, e.target.value))}
        placeholder={country.iso === 'IQ' ? '750 123 4567' : ''}
        className="flex-1"
      />
    </div>
  );
}
